import { useEffect, useMemo, useState } from "react";
import { MediaField } from "@/components/admin/MediaField";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ImportButtons } from "@/components/admin/import/ImportButtons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listAdminJournalArticles,
  createJournalArticle,
  updateJournalArticle,
  deleteJournalArticle,
  reorderJournalArticles,
  getPublicationState,
  type AdminJournalArticle,
} from "@/lib/admin/journal.functions";
import { journalFormSchema, type JournalArticleFormInput } from "@/lib/admin/journal.schemas";

export const Route = createFileRoute("/_admin/admin/journal")({
  component: JournalAdminPage,
});

type FormState = {
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  body: string;
  image_url: string;
  alt_text: string;
  author: string;
  read_time: string;
  published_at: string;
  status: "draft" | "published";
  is_demo: boolean;
};

function emptyForm(): FormState {
  return {
    slug: "",
    category: "",
    title: "",
    excerpt: "",
    body: "",
    image_url: "",
    alt_text: "",
    author: "",
    read_time: "",
    published_at: "",
    status: "draft",
    is_demo: false,
  };
}

function toLocalDateTimeInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromRow(r: AdminJournalArticle): FormState {
  return {
    slug: r.slug,
    category: r.category,
    title: r.title,
    excerpt: r.excerpt,
    body: (r.body ?? []).join("\n\n"),
    image_url: r.image_url ?? "",
    alt_text: r.alt_text ?? "",
    author: r.author,
    read_time: r.read_time,
    published_at: toLocalDateTimeInput(r.published_at),
    status: r.status,
    is_demo: r.is_demo,
  };
}

const REORDER_ERROR_MSG: Record<string, string> = {
  incomplete_id_list: "Reorder must include every article. Reloading the list.",
  duplicate_ids: "Reorder contained duplicate rows.",
  unknown_id: "Reorder referenced an unknown article.",
  empty_id_list: "Nothing to reorder.",
  forbidden: "You don't have permission to reorder articles.",
};

function JournalAdminPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listAdminJournalArticles);
  const createFn = useServerFn(createJournalArticle);
  const updateFn = useServerFn(updateJournalArticle);
  const deleteFn = useServerFn(deleteJournalArticle);
  const reorderFn = useServerFn(reorderJournalArticles);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin", "journal"],
    queryFn: () => listFn(),
    staleTime: 10_000,
  });

  const [editing, setEditing] = useState<AdminJournalArticle | "new" | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<AdminJournalArticle | null>(null);

  useEffect(() => {
    if (editing === "new") setForm(emptyForm());
    else if (editing) setForm(fromRow(editing));
  }, [editing]);

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "journal"] }),
      queryClient.invalidateQueries({ queryKey: ["journal", "public"] }),
    ]);

  const saveMutation = useMutation({
    mutationFn: async (payload: { id: string | null; patch: JournalArticleFormInput }) => {
      if (payload.id === null) {
        return createFn({ data: payload.patch as never });
      }
      return updateFn({ data: { id: payload.id, patch: payload.patch } as never });
    },
    onSuccess: async () => {
      toast.success("Article saved");
      setEditing(null);
      setErrors({});
      await invalidate();
    },
    onError: (err) => {
      console.error(err);
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("slug_taken")) {
        setErrors({ slug: "This slug is already in use." });
        toast.error("Slug already in use.");
        return;
      }
      toast.error(`Couldn't save the article: ${msg}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Article deleted");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: (err) => {
      console.error(err);
      toast.error("Couldn't delete the article.");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorderFn({ data: { ids } as never }),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "journal"] });
      const previous = queryClient.getQueryData<AdminJournalArticle[]>([
        "admin",
        "journal",
      ]);
      if (previous) {
        const map = new Map(previous.map((p) => [p.id, p]));
        const reordered = ids
          .map((id, i) => {
            const row = map.get(id);
            return row ? { ...row, sort_order: (i + 1) * 10 } : null;
          })
          .filter((r): r is AdminJournalArticle => r !== null);
        queryClient.setQueryData(["admin", "journal"], reordered);
      }
      return { previous };
    },
    onError: async (err, _vars, ctx) => {
      console.error(err);
      if (ctx?.previous) {
        queryClient.setQueryData(["admin", "journal"], ctx.previous);
      }
      const code = err instanceof Error ? err.message : "reorder_failed";
      toast.error(REORDER_ERROR_MSG[code] ?? "Couldn't reorder articles.");
      await queryClient.invalidateQueries({ queryKey: ["admin", "journal"] });
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  const articles = useMemo(() => data ?? [], [data]);

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= articles.length) return;
    const ids = articles.map((p) => p.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved);
    reorderMutation.mutate(ids);
  };

  const handleSave = () => {
    const parsed = journalFormSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !fe[key]) fe[key] = issue.message;
      }
      setErrors(fe);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setErrors({});
    const id = editing && editing !== "new" ? editing.id : null;
    saveMutation.mutate({ id, patch: parsed.data });
  };

  if (isLoading) {
    return (
      <div>
        <AdminPageHeader
          title="Journal"
          description="Manage the articles shown on the public Journal."
        />
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <AdminPageHeader title="Journal" />
        <Alert variant="destructive">
          <AlertDescription>
            Couldn't load articles.{" "}
            <button className="underline" onClick={() => refetch()}>Retry</button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader
        title="Journal"
        description="Create, edit, reorder and publish articles on the public /blog page."
        actions={<ImportButtons moduleId="journal" />}
      />

      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {articles.length} article{articles.length === 1 ? "" : "s"}
        </p>
        <Button onClick={() => setEditing("new")} className="gap-2">
          <Plus className="h-4 w-4" aria-hidden /> New article
        </Button>
      </div>

      {articles.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No articles yet. Create the first one.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3 w-24">Order</th>
                <th className="p-3">Title</th>
                <th className="p-3">Category</th>
                <th className="p-3">Status</th>
                <th className="p-3">Flags</th>
                <th className="p-3 w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((p, i) => {
                const state = getPublicationState(p);
                return (
                  <tr key={p.id} className="border-t border-border">
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => move(i, -1)}
                          disabled={i === 0 || reorderMutation.isPending}
                          aria-label="Move up"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => move(i, 1)}
                          disabled={i === articles.length - 1 || reorderMutation.isPending}
                          aria-label="Move down"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-foreground">{p.title}</div>
                      <div className="text-xs text-muted-foreground">/{p.slug} • {p.is_demo ? "Demo" : "Prod"}</div>
                    </td>
                    <td className="p-3 text-muted-foreground">{p.category}</td>
                    <td className="p-3">
                      {state === "published" ? (
                        <Badge variant="secondary">Published</Badge>
                      ) : state === "scheduled" ? (
                        <Badge variant="outline">Scheduled</Badge>
                      ) : (
                        <Badge variant="outline">Draft</Badge>
                      )}
                    </td>
                    <td className="p-3">
                      {p.is_demo ? <Badge variant="outline">Demo</Badge> : null}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setEditing(p)} aria-label="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setConfirmDelete(p)}
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing === "new" ? "New article" : "Edit article"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <FieldRow label="Title *" error={errors.title}>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </FieldRow>
            <FieldRow
              label="Slug *"
              hint="Lowercase letters, numbers and hyphens. Used in the article URL."
              error={errors.slug}
            >
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="my-article-slug"
              />
            </FieldRow>
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldRow label="Category *" error={errors.category}>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                />
              </FieldRow>
              <FieldRow label="Author" error={errors.author}>
                <Input
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                />
              </FieldRow>
            </div>
            <FieldRow label="Excerpt" error={errors.excerpt}>
              <Textarea
                rows={3}
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              />
            </FieldRow>
            <FieldRow
              label="Body"
              hint="Separate paragraphs with a blank line."
              error={errors.body}
            >
              <Textarea
                rows={10}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
              />
            </FieldRow>
            <FieldRow
              label="Image URL"
              hint="Optional. Must be a valid https:// URL. Leave blank to use a matching bundled image or the neutral placeholder."
              error={errors.image_url}
            >
              <MediaField
                value={form.image_url}
                onChange={(next) => setForm({ ...form, image_url: next })}
                onAltSuggestion={(alt) =>
                  setForm((prev) => (prev.alt_text.trim() ? prev : { ...prev, alt_text: alt }))
                }
              />
            </FieldRow>
            <FieldRow
              label="Alt text"
              hint="Optional. Leave blank to auto-derive from the title."
              error={errors.alt_text}
            >
              <Input
                value={form.alt_text}
                onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
              />
            </FieldRow>
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldRow label="Read time" error={errors.read_time}>
                <Input
                  value={form.read_time}
                  onChange={(e) => setForm({ ...form, read_time: e.target.value })}
                  placeholder="6 min read"
                />
              </FieldRow>
              <FieldRow
                label="Published at"
                hint="Leave blank to publish immediately when Status is Published. Future dates schedule the article."
                error={errors.published_at}
              >
                <Input
                  type="datetime-local"
                  value={form.published_at}
                  onChange={(e) => setForm({ ...form, published_at: e.target.value })}
                />
              </FieldRow>
            </div>
            <FieldRow label="Status" error={errors.status}>
              <select
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value as "draft" | "published" })
                }
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </FieldRow>
            <div className="flex items-center gap-3">
              <Switch
                id="is_demo"
                checked={form.is_demo}
                onCheckedChange={(v) => setForm({ ...form, is_demo: v })}
              />
              <Label htmlFor="is_demo">
                Demo content (shows the "Demo Content" chip)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this article?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes “{confirmDelete?.title}” permanently. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FieldRow({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="mb-1 block">{label}</Label>
      {children}
      {hint && !error ? (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      ) : null}
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}